import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Card, Row, Col, message, Popconfirm, Space } from 'antd';
import { ReloadOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useFinanceApi } from '../api/financeApi';
import { fmtAmount, fmtDate } from '../shared/format';
import { enhanceColumns } from '../../ortak/tableHelpers';

export default function FaturaEslestirmeTab({ perms }) {
  const api = useFinanceApi();
  const p = perms.fatura_eslestirme || {};
  const canEdit = p.edit !== false;

  const [unpaid, setUnpaid] = useState([]);
  const [unrec, setUnrec] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selFatura, setSelFatura] = useState([]);
  const [selTahsilat, setSelTahsilat] = useState(null);

  const loadAll = useCallback(async () => {
    if (p.view === false) return;
    setLoading(true);
    try {
      const [u, r, h] = await Promise.all([
        api.get('/invoice-matching/unpaid'),
        api.get('/invoice-matching/unrecognized'),
        api.get('/invoice-matching/history'),
      ]);
      setUnpaid(u); setUnrec(r); setHistory(h);
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (p.view === false) return <div style={{ padding: 24 }}>Görüntüleme yetkiniz yok.</div>;

  const doMatch = async () => {
    if (!selFatura.length || !selTahsilat) { message.warning('Fatura(lar) ve bir tahsilat seçin'); return; }
    try {
      await api.post('/invoice-matching/match', { fatura_ids: selFatura, tahsilat_id: selTahsilat });
      message.success('Eşleştirildi');
      setSelFatura([]); setSelTahsilat(null);
      loadAll();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Eşleştirme hatası');
    }
  };

  const doUnmatch = async (id) => {
    try {
      await api.post('/invoice-matching/unmatch', { eslestirme_id: id });
      message.success('Geri alındı');
      loadAll();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Geri alma hatası');
    }
  };

  const faturaCols = [
    { title: 'Evrak No', dataIndex: 'evrakNo' },
    { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true },
    { title: 'Banka', dataIndex: 'banka_ad', ellipsis: true },
    { title: 'Tutar', dataIndex: 'fatura_tutar', render: (v) => fmtAmount(v) },
    { title: 'Tarih', dataIndex: 'fatura_tarih', render: fmtDate },
  ];
  const tahsilatCols = [
    { title: 'Tarih', dataIndex: 'tarih', render: fmtDate },
    { title: 'Cari', dataIndex: 'cari_ad', ellipsis: true },
    { title: 'Firma', dataIndex: 'firma_adi', ellipsis: true },
    { title: 'Tutar', dataIndex: 'tutar', render: (v, r) => fmtAmount(v, r.doviz) },
  ];
  const histCols = [
    { title: 'Evrak No', dataIndex: 'evrakNo' },
    { title: 'Fatura Tutar', dataIndex: 'fatura_tutar', render: (v) => fmtAmount(v) },
    { title: 'Tahsilat Tutar', dataIndex: 'tahsilat_tutar', render: (v) => fmtAmount(v) },
    { title: 'Tarih', dataIndex: 'eslestirme_tarihi', render: fmtDate },
    canEdit && {
      title: 'İşlem', key: 'a', width: 120,
      render: (_, r) => (
        <Popconfirm title="Geri alınsın mı?" okText="Evet" cancelText="Hayır" onConfirm={() => doUnmatch(r.id)}>
          <Button size="small" danger icon={<DisconnectOutlined />}>Geri Al</Button>
        </Popconfirm>
      ),
    },
  ].filter(Boolean);

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={loadAll}>Yenile</Button>
        {canEdit && (
          <Button type="primary" icon={<LinkOutlined />} onClick={doMatch}
            disabled={!selFatura.length || !selTahsilat}>
            Eşleştir ({selFatura.length} fatura → 1 tahsilat)
          </Button>
        )}
      </Space>
      <Row gutter={12}>
        <Col xs={24} lg={14}>
          <Card size="small" title="Tahsil Edilmemiş Faturalar" loading={loading}>
            <Table rowKey="id" size="small" dataSource={unpaid} columns={enhanceColumns(faturaCols)}
              pagination={{ pageSize: 20 }} scroll={{ x: 'max-content', y: 320 }}
              rowSelection={{
                selectedRowKeys: selFatura,
                onChange: (keys) => setSelFatura(keys),
                getCheckboxProps: () => ({ disabled: !canEdit }),
              }} />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card size="small" title="Tanınmamış Tahsilatlar" loading={loading}>
            <Table rowKey="id" size="small" dataSource={unrec} columns={enhanceColumns(tahsilatCols)}
              pagination={{ pageSize: 20 }} scroll={{ x: 'max-content', y: 320 }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selTahsilat ? [selTahsilat] : [],
                onChange: (keys) => setSelTahsilat(keys[0]),
                getCheckboxProps: () => ({ disabled: !canEdit }),
              }} />
          </Card>
        </Col>
      </Row>
      <Card size="small" title="Son Eşleştirmeler" style={{ marginTop: 12 }} loading={loading}>
        <Table rowKey="id" size="small" dataSource={history} columns={enhanceColumns(histCols)}
          pagination={{ pageSize: 10 }} scroll={{ x: 'max-content' }} />
      </Card>
    </div>
  );
}
